import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { MenuItem } from "@/types/menu";
import { MenuCategory } from "@/types/menu";
import { Order, OrderItem, OrderStatus } from "@/types/orders";
import { useToast } from "@/hooks/use-toast";
import {
  trackTableScan,
  getActiveOrdersForTable,
} from "@/services/tables/tableService";

interface MenuItemWithCategory extends MenuItem {
  categoryIds: string[];
  categories?: MenuCategory[];
}

export function usePublicMenu(tableId: string | null) {
  const [loading, setLoading] = useState(true);
  const [menuItems, setMenuItems] = useState<MenuItemWithCategory[]>([]);
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [storeName, setStoreName] = useState<string | null>(null);
  const [storeId, setStoreId] = useState<string | null>(null);
  const [tableName, setTableName] = useState<string | null>(null);
  const [tableNumber, setTableNumber] = useState<number | null>(null);
  const [activeOrders, setActiveOrders] = useState<Order[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const { toast } = useToast();

  // Function to refresh active orders
  const refreshActiveOrders = async () => {
    if (!tableId) return [];

    try {
      const activeOrdersData = await getActiveOrdersForTable(tableId);

      if (activeOrdersData && activeOrdersData.length > 0) {
        // Enhanced: Fetch order items with images
        const enhancedOrders = await Promise.all(
          activeOrdersData.map(async (rawOrder) => {
            // Get order items
            const { data: orderItems, error: itemsError } = await supabase
              .from("order_items")
              .select("*")
              .eq("order_id", rawOrder.id);

            if (itemsError) {
              console.error("Error fetching order items:", itemsError);
              return rawOrder;
            }

            // Get menu item images for order items
            const menuItemIds = orderItems
              .filter((item) => item.menu_item_id)
              .map((item) => item.menu_item_id);

            let imageMap = new Map();
            if (menuItemIds.length > 0) {
              const { data: menuItems } = await supabase
                .from("menu_items")
                .select("id, image_url")
                .in("id", menuItemIds);

              imageMap = new Map(
                (menuItems || []).map((item) => [item.id, item.image_url])
              );
            }

            // Add image URLs to order items
            const itemsWithImages = orderItems.map((item) => ({
              ...item,
              imageUrl: item.menu_item_id
                ? imageMap.get(item.menu_item_id)
                : undefined,
            }));

            // Update the order with complete items data
            return {
              ...rawOrder,
              items: itemsWithImages,
            };
          })
        );

        // Format the orders to match our Order type
        const formattedOrders: Order[] = enhancedOrders.map((rawOrder) => {
          const formattedOrder: Order = {
            id: rawOrder.id,
            store_id: rawOrder.store_id,
            table_id: rawOrder.table_id,
            tableNumber: rawOrder.tableNumber,
            tableName: rawOrder.tableName,
            status: rawOrder.status as OrderStatus,
            totalAmount: rawOrder.totalAmount,
            items: Array.isArray(rawOrder.items)
              ? rawOrder.items.map((item: OrderItem) => ({
                  id: item.id,
                  order_id: item.order_id,
                  menu_item_id: item.menu_item_id,
                  menu_item_name: item.menu_item_name,
                  price: item.price,
                  quantity: item.quantity,
                  notes: item.notes,
                  created_at: item.created_at,
                  updated_at: item.updated_at,
                  imageUrl: item.imageUrl,
                }))
              : [],
            specialInstructions: rawOrder.specialInstructions,
            createdAt: rawOrder.createdAt,
            updatedAt: rawOrder.updatedAt,
            is_payed: rawOrder.is_payed,
          };

          // Ensure createdAt and updatedAt are valid date strings
          if (
            !formattedOrder.createdAt ||
            isNaN(new Date(formattedOrder.createdAt).getTime())
          ) {
            formattedOrder.createdAt = new Date().toISOString();
          }

          if (
            !formattedOrder.updatedAt ||
            isNaN(new Date(formattedOrder.updatedAt).getTime())
          ) {
            formattedOrder.updatedAt = new Date().toISOString();
          }

          return formattedOrder;
        });

        setActiveOrders(formattedOrders);
        return formattedOrders;
      } else {
        setActiveOrders([]);
        return [];
      }
    } catch (error) {
      console.error("Error refreshing active orders:", error);
      return [];
    }
  };

  useEffect(() => {
    // Reset state when tableId changes
    setLoading(true);
    setError(null);
    setMenuItems([]);
    setCategories([]);
    setActiveOrders([]);
    setAvailableTags([]);

    if (!tableId || tableId.trim() === "") {
      setError("No table ID provided");
      setLoading(false);
      return;
    }

    const fetchStoreAndMenuItems = async () => {
      try {
        // First, check if the tableId is actually a store ID
        const { data: storeData, error: storeError } = await supabase
          .from("stores")
          .select("id, name")
          .eq("id", tableId)
          .maybeSingle();

        if (storeData) {
          // This is a store ID, not a table ID
          setError(
            `This appears to be a store ID (${storeData.name}), not a table ID. Please scan a table QR code instead.`
          );
          setLoading(false);
          return;
        }

        // Track the scan with our updated function
        const scanResult = await trackTableScan(tableId);

        if (!scanResult.success) {
          console.error("Error tracking scan:", scanResult.error);
          if (scanResult.error?.includes("Invalid table ID")) {
            setError(
              `This QR code appears to be invalid. It might be for something other than a table. Please scan a valid table QR code.`
            );
            setLoading(false);
            return;
          }
          toast({
            title: "Notice",
            description: "Unable to track table visit: " + scanResult.error,
            variant: "destructive",
            duration: 3000,
          });
        } 
        // Get the table to find the store ID
        const { data: tableData, error: tableError } = await supabase
          .from("store_tables")
          .select("store_id, number, name, stores(id, name)")
          .eq("id", tableId)
          .maybeSingle();

        if (tableError) {
          console.error("Error fetching table:", tableError);
          setError(`Error fetching table: ${tableError.message}`);
          setLoading(false);
          return;
        }

        if (!tableData) {
          console.error("Table not found");
          setError(
            `Table with ID ${tableId} not found. Please check the QR code.`
          );
          setLoading(false);
          return;
        }

        if (tableData.stores) {
          setStoreName(tableData.stores.name);
          setStoreId(tableData.stores.id);
        }
        setTableName(tableData.name);
        setTableNumber(tableData.number);

        // Get store_id from the table
        const storeId = tableData.store_id;

        if (!storeId) {
          setError("This table is not associated with any store");
          setLoading(false);
          return;
        }

        // Check for active orders - critical for seeing orders on the public menu
        await refreshActiveOrders();

        // Get categories for this store
        const { data: categoryData, error: categoryError } = await supabase
          .from("menu_categories")
          .select("*")
          .eq("store_id", storeId)
          .order("position");

        if (categoryError) {
          console.error("Error fetching categories:", categoryError);
        } else if (categoryData) {
          setCategories(categoryData);
        }

        // Now get the menu items for this store
        const { data: items, error: menuError } = await supabase
          .from("menu_items")
          .select("*")
          .eq("store_id", storeId);

        if (menuError) {
          console.error("Error fetching menu items:", menuError);
          setError(`Failed to load menu items: ${menuError.message}`);
          setLoading(false);
          return;
        }

        if (!items || items.length === 0) {
          setMenuItems([]);
          setLoading(false);
          return;
        }

        // Process menu items to match MenuItem type
        const processedItems = await Promise.all(
          items.map(async (item) => {
            try {
              // Get categories for this item
              const { data: itemCategories } = await supabase
                .from("menu_items_categories")
                .select("category_id")
                .eq("menu_item_id", item.id);

              // Get tags for this item
              const { data: tags } = await supabase
                .from("menu_items_tags")
                .select("menu_tags(name)")
                .eq("menu_item_id", item.id);

              const tagNames = tags?.map((t) => t.menu_tags?.name || "") || [];

              return {
                ...item,
                categoryIds: itemCategories?.map((c) => c.category_id) || [],
                tags: tagNames,
              } as MenuItemWithCategory;
            } catch (error) {
              console.error("Error processing menu item:", item.id, error);
              // Return the basic item without the related data
              return {
                ...item,
                categoryIds: [],
                tags: [],
              } as MenuItemWithCategory;
            }
          })
        );

        // Collect all available tags
        const allTags = new Set<string>();
        processedItems.forEach((item) => {
          (item.tags || []).forEach((tag) => allTags.add(tag));
        });

        setAvailableTags(Array.from(allTags));
        setMenuItems(processedItems);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching menu:", error);
        setError(
          `Failed to load menu. Error: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
        setLoading(false);
      }
    };

    fetchStoreAndMenuItems();
  }, [tableId, toast]);

  // Set up order subscription to detect new orders
  useEffect(() => {
    if (!tableId) return;

    const orderSubscription = supabase
      .channel("public:orders")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "orders",
          filter: `table_id=eq.${tableId}`,
        },
        (payload) => {
          refreshActiveOrders();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(orderSubscription);
    };
  }, [tableId]);

  return {
    loading,
    error,
    storeName,
    storeId,
    tableName,
    tableNumber,
    menuItems,
    categories,
    activeOrders,
    availableTags,
    refreshActiveOrders,
  };
}

export type { MenuItemWithCategory };
